import type { HTMLAttributes, ReactNode } from 'react';
import clsx from 'clsx';
import styles from './Card.module.css';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** Header content — rendered with a bottom divider. */
  header?: ReactNode;
  /** Footer content — rendered with a top divider. */
  footer?: ReactNode;
  /** Visually flatten — no padding, header/footer dividers preserved. */
  flat?: boolean;
  /** Interactive cards get hover affordance + focus ring. */
  interactive?: boolean;
}

/**
 * Surface primitive — 1px border on white, no shadow (per §9.5).
 *
 * Compose with header / footer slots, or pass children directly for body-only.
 * Use `interactive` when the whole card is clickable (list rows etc.).
 */
export function Card({
  header,
  footer,
  flat,
  interactive,
  className,
  children,
  ...rest
}: CardProps) {
  return (
    <div
      className={clsx(
        styles.card,
        interactive && styles.interactive,
        className,
      )}
      {...rest}
    >
      {header && <div className={styles.header}>{header}</div>}
      <div className={clsx(styles.body, flat && styles.flat)}>{children}</div>
      {footer && <div className={styles.footer}>{footer}</div>}
    </div>
  );
}
